import { Callout, Collapsible, Separator, Skeleton } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import { isActionAvailable } from "@/utils/available-actions";
import { GitPullRequestArrow } from "lucide-react";
import { Fragment, useState } from "react";
import {
  CareplanAvailableAction,
  useCarePlanSummary,
} from "../network/useCarePlanSummary";
import { useDisposition } from "../network/useDisposition";
import { AddDisposition } from "./AddDisposition";
import { Disposition } from "./Disposition";

interface DispositionsProps {
  carePlanId: string;
  encounterId: string;
  totalDispositions: number;
}

export const Dispositions = ({
  carePlanId,
  encounterId,
  totalDispositions,
}: DispositionsProps) => {
  const [open, setOpen] = useState(false);
  const { data, isFetching, isError } = useDisposition({
    encounterId,
    carePlanId,
    enabled: open,
  });

  const { data: careplanSummary } = useCarePlanSummary(encounterId);
  const careplanAvailableActions = careplanSummary?.availableActions;

  const canWriteDisposition = isActionAvailable(
    CareplanAvailableAction.ADD_DISPOSITION,
    careplanAvailableActions,
  );

  const canReadDisposition = isActionAvailable(
    CareplanAvailableAction.READ_DISPOSITION,
    careplanAvailableActions,
  );

  const handleOpenChange = (open: boolean) => {
    trackEvent(
      open ? TRACKERS.EXPAND_DISPOSITION : TRACKERS.COLLAPSE_DISPOSITION,
    );
    setOpen(open);
  };

  if (!canReadDisposition) {
    return null;
  }

  if (isError) {
    return (
      <Callout
        className="w-full"
        variant="danger"
        title="Something went wrong!"
        description="Failed to load disposition."
      />
    );
  }

  return (
    <Collapsible
      title="Disposition"
      titleIcon={<GitPullRequestArrow className="w-4 h-4" />}
      headerSize="sm"
      onOpenChange={handleOpenChange}
      status={totalDispositions ? `${totalDispositions.toString()}` : "None"}
    >
      <div className="flex flex-col gap-2">
        {isFetching ? (
          <div className="grid gap-2">
            {Array.from({ length: 3 }).map((_, i) => (
              //  biome-ignore lint/suspicious/noArrayIndexKey: unneeded for loading skeleton
              <Skeleton className="h-6" key={i} />
            ))}
          </div>
        ) : (
          data?.dispositionTasks.map((task) => {
            return (
              <Fragment key={task.id}>
                <Disposition
                  key={task.id}
                  carePlanId={carePlanId}
                  encounterId={encounterId}
                  task={task}
                  disabled={!canWriteDisposition}
                />
                <Separator />
              </Fragment>
            );
          })
        )}
        <AddDisposition carePlanId={carePlanId} encounterId={encounterId} />
      </div>
    </Collapsible>
  );
};
